// NYT format (from doshea/nyt_crosswords repo)
export interface NYTPuzzle {
  title: string;
  author: string;
  editor: string;
  date: string;
  size: { rows: number; cols: number };
  grid: string[]; // Flat array, "." = black cell
  gridnums: number[]; // Clue numbers (0 if none)
  clues: {
    across: string[]; // "1. Clue text (4)"
    down: string[];
  };
  answers: {
    across: string[];
    down: string[];
  };
}

// Guardian format (what the component needs)
export interface GuardianPuzzle {
  id: string;
  number: number;
  name: string;
  creator?: { name: string; webUrl: string };
  date: number;
  entries: GuardianEntry[];
  dimensions: { rows: number; cols: number };
  crosswordType: 'cryptic' | 'everyman' | 'prize' | 'quick-cryptic' | 'quick' | 'quiptic' | 'special' | 'speedy' | 'sunday-quick' | 'weekend' | 'mini';
  solutionAvailable: boolean;
}

export interface GuardianEntry {
  id: `${number}-across` | `${number}-down`;
  number: number;
  humanNumber: string;
  clue: string;
  direction: "across" | "down";
  length: number;
  group: [`${number}-across` | `${number}-down`, ...(`${number}-across` | `${number}-down`)[]];
  position: { x: number; y: number };
  separatorLocations: Record<string, number[]>;
  solution?: string;
}

/**
 * Parse separator hints from clue text like "(2-2)" or "(3,4,5)"
 */
function parseSeparators(clue: string): Record<string, number[]> {
  const match = clue.match(/\(([0-9,\-\s]+)\)\s*$/);
  if (!match) return {};

  const separators: Record<string, number[]> = {};
  const pattern = match[1].replace(/\s/g, "");
  let position = 0;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    if (char === "-" || char === ",") {
      if (!separators[char]) separators[char] = [];
      separators[char].push(position);
    } else if (/\d/.test(char)) {
      // Handle multi-digit numbers
      let numStr = char;
      while (i + 1 < pattern.length && /\d/.test(pattern[i + 1])) {
        numStr += pattern[++i];
      }
      position += parseInt(numStr, 10);
    }
  }

  return separators;
}

/**
 * Extract clue number and text from NYT clue format
 */
function parseClue(clueStr: string): { number: number; text: string } | null {
  const match = clueStr.match(/^(\d+)\.\s*(.+)$/);
  if (!match) return null;
  return {
    number: parseInt(match[1], 10),
    text: match[2],
  };
}

/**
 * Convert NYT puzzle format to Guardian format
 */
export function convertNYTToGuardian(nyt: NYTPuzzle): GuardianPuzzle {
  const { rows, cols } = nyt.size;
  const entries: GuardianEntry[] = [];

  // Build 2D arrays for easier lookup
  const grid: string[][] = [];
  const gridnums: number[][] = [];
  for (let r = 0; r < rows; r++) {
    grid[r] = nyt.grid.slice(r * cols, (r + 1) * cols);
    gridnums[r] = nyt.gridnums.slice(r * cols, (r + 1) * cols);
  }

  // Helper to find position of clue number
  const findCluePosition = (num: number): { x: number; y: number } => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (gridnums[r][c] === num) {
          return { x: c, y: r };
        }
      }
    }
    return { x: 0, y: 0 };
  };

  // Process across clues
  nyt.clues.across.forEach((clueStr, idx) => {
    const parsed = parseClue(clueStr);
    if (!parsed) return;

    const solution = nyt.answers.across[idx];
    const pos = findCluePosition(parsed.number);

    entries.push({
      id: `${parsed.number}-across`,
      number: parsed.number,
      humanNumber: String(parsed.number),
      clue: parsed.text,
      direction: "across",
      length: solution.length,
      group: [`${parsed.number}-across`],
      position: pos,
      separatorLocations: parseSeparators(parsed.text),
      solution,
    });
  });

  // Process down clues
  nyt.clues.down.forEach((clueStr, idx) => {
    const parsed = parseClue(clueStr);
    if (!parsed) return;

    const solution = nyt.answers.down[idx];
    const pos = findCluePosition(parsed.number);

    entries.push({
      id: `${parsed.number}-down`,
      number: parsed.number,
      humanNumber: String(parsed.number),
      clue: parsed.text,
      direction: "down",
      length: solution.length,
      group: [`${parsed.number}-down`],
      position: pos,
      separatorLocations: parseSeparators(parsed.text),
      solution,
    });
  });

  // Sort entries by number, then by direction
  entries.sort((a, b) => {
    if (a.number !== b.number) return a.number - b.number;
    return a.direction === "across" ? -1 : 1;
  });

  return {
    id: `nyt/${nyt.date}`,
    number: 1,
    name: nyt.title || `NYT Crossword - ${nyt.date}`,
    creator: nyt.author ? { name: nyt.author, webUrl: "" } : undefined,
    date: new Date(nyt.date).getTime(),
    entries,
    dimensions: { rows, cols },
    crosswordType: "cryptic",
    solutionAvailable: true,
  };
}

/**
 * Calculate grid dimensions from Guardian puzzle entries
 */
export function calculateDimensions(
  puzzle: GuardianPuzzle
): { rows: number; cols: number } {
  return puzzle.dimensions;
}
